/**
 *
 * @module coreStorageProvider
 *
 */

import Check from 'check-types';
import Logging from './../../../logging/entry';
import Exception from './../../exception/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ProviderException = Exception.ProviderException;

const {
    assigned
} = Check.assert;

/**
 *
 * @class CoreStorageProvider
 * @implements {IStorage}
 * @desc Core Storage provider implementation definition. Acts as a base definition that
 * is then extended by platform specific implementations.
 * @note in this implementation the key is expected to be a String, which differs
 * from other implementations that accept various types i.e. Number, Undefined, etc...
 *
 */
export default class CoreStorageProvider {

    /**
     *
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(logger = Logging) {

        /**
         *
         * @access private
         * @type {Object}
         * @desc storage reference
         *
         */
        this.store = null;

        /**
         *
         * @access private
         * @type {Array}
         * @desc tracks stored keys
         *
         */
        this.storedKeys = [];

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

    }

    /**
     *
     * @access public
     * @method set
     * @param {String} key
     * @param {Object} value
     * @desc sets a key/value pair in storage, returns cached data if key already exists.
     * @returns {Promise<Void>}
     *
     * @example
     * storage.set('some-key', { 'item': 'data' }).then(() => {});
     *
     */
    set(key, value) {

        const method = 'Storage.set(key, value)';

        return this.op(method, key, value);

    }

    /**
     *
     * @access public
     * @method get
     * @param {String} key
     * @desc retrieves a given key from storage.
     * @returns {Promise<String>}
     *
     * @example
     * storage.get('some-key').then((value) => { console.log(value); });
     *
     */
    get(key) {

        const method = 'Storage.get(key)';

        return this.op(method, key);

    }

    /**
     *
     * @access public
     * @method remove
     * @param {String} key
     * @desc removes a given key from storage.
     * @returns {Promise<Void>}
     *
     * @example
     * storage.remove('some-key').then(() => { console.log('key removed'); });
     *
     */
    remove(key) {

        const method = 'Storage.remove(key)';

        return this.op(method, key);

    }

    /**
     *
     * @access public
     * @method clear
     * @desc removes all known keys from storage, items added outside of the
     * current Storage instance will be unaffected.
     * @returns {Promise<Void>}
     *
     * @example
     * storage.clear().then(() => { console.log('all SDK keys removed'); });
     *
     */
    clear() {

        const method = 'Storage.clear()';

        return this.op(method);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {String} signature - the current active method signature
     * @param {String} key - Storage key reference
     * @param {*} value - value to store with key
     * @throws {InvalidArgumentException}
     * @throws {ProviderException}
     * @returns {Promise<*>}
     *
     */
    op(signature, key, value) {

        const errorMessage = {
            get operationError() {
                return `${signature} cannot complete successfully`;
            },
            get keyError() {
                return `${signature} key should be a String with length`;
            },
            get keyMissingWarning() {
                return `${signature} reference cache key "${key}" does not exist`;
            },
            get valueParseError() {
                return `${signature} unable to parse value`;
            },
            get storeError() {
                return `${signature} missing expected store reference`;
            }
        };

        const operation = signature.slice(signature.indexOf('.') + 1, signature.indexOf('('));
        const isClear = Check.equal(operation, 'clear');
        const isRemove = Check.equal(operation, 'remove');

        const store = this.store;

        assigned(store, errorMessage.storeError, ProviderException);

        return new Promise((resolve, reject) => {

            const { logger, storedKeys } = this;

            /**
             *
             * @desc verify key validity for all operations except clear
             *
             */
            if (Check.equal(isClear, false) && (Check.not.string(key) || Check.emptyString(key))) {
                return reject(new InvalidArgumentException(errorMessage.keyError));
            }

            /**
             *
             * @desc process given operation
             * @note all items set in Storage are expected to be stringified values
             * @note JSON operations are processed in a try...catch
             * @note if a given key is not found, fail silently and display warning
             *
             */
            try {

                if (isClear) {

                    // remove all known keys
                    storedKeys.map((keyInStorage) => {
                        return store.removeItem(keyInStorage);
                    });

                    return resolve();

                }

                if (isRemove) {

                    // remove item and key from reference
                    storedKeys.splice(storedKeys.indexOf(key), 1);

                    return resolve(store.removeItem(key));

                } else if (Check.not.undefined(value)) {

                    // set item if valid value provided
                    // stringify before updating stored keys reference for accuracy
                    const val = JSON.stringify(value);

                    storedKeys.push(key);

                    return resolve(store.setItem(key, val));

                } else {

                    // if valid value not provided, attempt to get cached item
                    try {

                        const item = store.getItem(key);

                        if (Check.not.assigned(item)) {
                            logger.warn('Storage', errorMessage.keyMissingWarning);
                            return resolve();
                        }

                        return resolve(JSON.parse(item));

                    } catch (error) {

                        return reject(new InvalidArgumentException(`${errorMessage.valueParseError} - ${error}`));

                    }

                }

            } catch (error) {

                return reject(new ProviderException(`${errorMessage.operationError} - ${error}`));

            }

        });

    }

}



// WEBPACK FOOTER //
// ./src/services/platforms/shared/coreStorageProvider.js